import { PatientPortalAccess } from '../models/patient-portal-access';
import { isActiveLink } from './proxy-access.utils';

/** One rendered chip: the words staff read and the global `.badge--*` modifier that colours it. */
export interface AccessStatusBadge {
  label: string;
  class: string;
}

/**
 * The chip per access state. The colours are the global `.badge--*` utilities the request queues
 * already use, so a status reads the same wherever staff meet it.
 *
 * `NoAccess` takes the neutral treatment rather than the red one: a patient nobody has linked an
 * account to has not been blocked, and colouring the two alike would have staff chasing a security
 * decision that was never made.
 */
export const ACCESS_STATUS_BADGES = {
  Active: { label: 'Active', class: 'badge--approved' },
  Blocked: { label: 'Blocked', class: 'badge--denied' },
  NoAccess: { label: 'No portal access', class: 'badge--unknown' },
} as const satisfies Record<string, AccessStatusBadge>;

export type AccessStatus = keyof typeof ACCESS_STATUS_BADGES;

/**
 * The chip shown while the access state is unknown — before the read lands, and after one that
 * failed.
 *
 * Deliberately not the Active treatment: a green "Active" chip on a patient who turns out to be
 * blocked is the worst failure any screen showing this can have, and a screen that never found out
 * must say so rather than guess the reassuring answer.
 */
export const UNKNOWN_ACCESS_STATUS_BADGE: AccessStatusBadge = {
  label: 'Status unavailable',
  class: 'badge--unknown',
};

/**
 * Which of the three states a patient's portal access is in, or null when it isn't known.
 *
 * A block wins over everything: `isBlocked` is a property of the patient and leaves every link row
 * Active, so reading the links alone would show a blocked patient as "Active". With no block in
 * force the question is whether anyone can currently get in, which is whether any link is still
 * Active; a patient whose only links are revoked has no portal access, and says so.
 *
 * Lives here rather than on the Access & Controls detail screen because the audit-log screens show
 * the same chip for the same patient: one patient's access state must not read
 * differently depending on which screen a staff member happens to be on.
 */
export function getAccessStatus(access: PatientPortalAccess | null): AccessStatus | null {
  if (!access) {
    return null;
  }
  if (access.isBlocked) {
    return 'Blocked';
  }
  return access.linkedUsers.some(isActiveLink) ? 'Active' : 'NoAccess';
}

/** The chip for an access record, falling back to {@link UNKNOWN_ACCESS_STATUS_BADGE}. */
export function getAccessStatusBadge(access: PatientPortalAccess | null): AccessStatusBadge {
  const status = getAccessStatus(access);
  return status ? ACCESS_STATUS_BADGES[status] : UNKNOWN_ACCESS_STATUS_BADGE;
}
